import React, { useRef, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames/bind';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { findMobileOS, getPathFromUrl } from 'yoda-core-components/lib/helpers/Utils/Utils';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import List from 'yoda-core-components/lib/components/List/List';
import {
    getScrolledElementOffSet,
    getDefaultOffSet,
    setDefaultOffSet,
} from '../../helpers/Utils/scrollPosition';
import { isGalleryPage } from '../../helpers/Utils/pageType';
import ProductCardConnected from '../ProductCard/ProductCard';
import { cardTypes } from '../ProductCard/ProductCard.config';
import { triggerEnsightenEvent } from '../../actions/ProductCartridgeAction';
import UrgencyMessage from '../UrgencyMessageContainer/UrgencyMessageLazy';
import * as styles from './YodaProductCartridge.css';
import Carousel from '../Carousel';
import { selectUrgencyMessages } from '../../selectors/fromState';
import {
    selectContext,
    selectDeviceType,
    selectIsNative,
    selectFeatureFlags,
    selectPreferencesRecommendations,
} from '../../selectors/ContextSelector';
import useProductCartridgeEffect from './useProductCartridgeEffect';

const cx = classNames.bind(styles);

/* istanbul ignore next */
const YodaProductCartridge = ({
    productCartridgeList,
    enableUrgencyMessagingOnATC,
    slotId,
    onPaneClick,
    isPLAEnabled,
    showPaneLoader,
    showImageLoader,
    pricingConfig,
    pricingConfig: { disableFPACAPI },
    showPricing,
    productItemClass,
    isCouponRecommendationsModal,
    criteriaName,
    enableGridViewInCertona,
    customRenderProductCard,
    cartridgeListRenderer,
    highlightCardSlot,
    panelType,
    cartridgeHeader,
    panelTitle,
    productCatridgeTheme,
    hasTitle,
    fromMiniPDP,
    fromHotDeals,
    fromCart,
    pageType,
}) => {
    const [quickAddToCartRefs, setQuickAddToCartRefs] = useState({});

    const context = useSelector(selectContext);
    const urgencyMessages = useSelector(selectUrgencyMessages);
    const deviceType = useSelector(selectDeviceType);
    const isNative = useSelector(selectIsNative);
    const { disableRRZoneLazyLoad = false, pdpBreadCrumbzScrollTop = true } = useSelector(
        selectFeatureFlags
    );
    const recommendations = useSelector(selectPreferencesRecommendations);
    let cartridgeRef = useRef(null);

    const dispatch = useDispatch();
    const history = useHistory();
    const {
        location: { pathname = '' },
    } = history;

    const [imageHeight, setImgHeight] = useState(null);

    const setLocalStorageData = (key, value) => {
        try {
            LocalStorage.setData(key, value);
        } catch (error) {
            console.error(`Failed to set data in LocalStorage: ${error}`);
        }
    };

    const handleOnPaneClick = (e, dataItem, linkUrlWithSlotId) => {
        const { index = 0, id = 0 } = dataItem;
        if (onPaneClick) {
            e.preventDefault();
            e.stopPropagation();
            onPaneClick(
                index,
                linkUrlWithSlotId,
                productCartridgeList[index],
                quickAddToCartRefs[id] && quickAddToCartRefs[id].contains(e.target)
            );
        }

        const isGallery = isGalleryPage(pathname);
        if (isGallery) {
            const scrollKeyVal = {
                galleryScrollTo: slotId,
            };
            const scrollKey = { key: window.location.href, val: scrollKeyVal };
            scrollKeyVal && setLocalStorageData('scrollToCartridge', scrollKey, true, '', false);
            // this is to set exact position where user clicked
            setLocalStorageData('galleryScrollToOffSet', getScrolledElementOffSet());
            isNative &&
                findMobileOS() === 'iOS' &&
                setDefaultOffSet(id, 'nativeGalleryScrollToOffSet');
        }
    };

    const defaultProductCardRenderer = (dataItem, index) => {
        const {
            linkURL = '',
            imageURL = '',
            name = '',
            rating = 0,
            reviews = '',
            skuId = '',
            skuSwatch = [],
            priceDetails = {},
            id = null,
            fromMiniPDP = false,
        } = dataItem;

        const recommendationsMap = get(recommendations, 'adobeMap', {});
        const rrPlacementType = recommendationsMap[slotId] || slotId;
        const linkUrlWithSlotId = getLinkUrlWithSlotId(linkURL, rrPlacementType, criteriaName);
        const linkwithoutHostName = getPathFromUrl(linkUrlWithSlotId);
        const currentUrgencyMessage = enableUrgencyMessagingOnATC ?? get(urgencyMessages, id, {});
        const productCardProps = getProductCardProps(
            imageURL,
            name,
            priceDetails,
            id,
            rating,
            reviews,
            skuId,
            skuSwatch,
            fromMiniPDP,
            index,
            enableGridViewInCertona,
            disableRRZoneLazyLoad,
            isPLAEnabled,
            isCouponRecommendationsModal,
            enableGridViewInCertona
        );

        return (
            <a
                href={linkwithoutHostName}
                className={getLinkClass(isCouponRecommendationsModal)}
                data-automation-id="reczone-link"
                onClick={(e) => handleOnPaneClick(e, dataItem, linkwithoutHostName)}
            >
                {renderUrgencyMessage(currentUrgencyMessage)}
                <ProductCardConnected {...productCardProps} />
            </a>
        );
    };

    const getLinkUrlWithSlotId = useCallback(
        (linkURL, rrPlacementType, criteriaName) => {
            let linkUrlWithSlotId = linkURL;
            if (slotId && linkURL) {
                linkUrlWithSlotId = linkURL.concat(`&rrplacementtype=${rrPlacementType}`);
            }
            return criteriaName
                ? `${linkUrlWithSlotId}&criteriaName=${criteriaName}`
                : linkUrlWithSlotId;
        },
        [slotId]
    );

    const getProdCardStyle = useCallback(
        (enableNewPriceFormat) =>
            cx('productCard', {
                cartridgeNewPriceWrapper: enableNewPriceFormat,
            }),
        []
    );

    const getLinkClass = useCallback(
        (isCouponRecommendationsModal) =>
            cx('linkColor', 'productPanelLinkBlock', productItemClass, {
                couponRecommendationsPaneLink: isCouponRecommendationsModal,
            }),
        [productItemClass]
    );

    const renderUrgencyMessage = useCallback(
        (currentUrgencyMessage) =>
            !isEmpty(currentUrgencyMessage) && (
                <span>
                    <UrgencyMessage
                        urgencyStatements={currentUrgencyMessage}
                        className={cx('urgencyMessage')}
                    />
                </span>
            ),
        []
    );

    const getProductCardProps = (
        imageURL,
        name,
        priceDetails,
        id,
        rating,
        reviews,
        skuId,
        skuSwatch,
        fromMiniPDP,
        index,
        enableGridViewInCertona,
        disableRRZoneLazyLoad,
        isPLAEnabled,
        isCouponRecommendationsModal
    ) => ({
        lazyLoadImages: !enableGridViewInCertona && !disableRRZoneLazyLoad,
        customClass: getProdCardStyle(!disableFPACAPI),
        imageUrl: imageURL,
        title: !isPLAEnabled ? name : null,
        price: priceDetails,
        pricingConfig,
        key: id,
        cardType: cardTypes?.GRID,
        rating: !isPLAEnabled ? Number(rating) : 0,
        reviewCount: !isPLAEnabled ? reviews : 0,
        cardTheme: 'cartridge',
        cardCustomClass: cx({ miniPdpCard: fromMiniPDP }),
        skuId,
        skuSwatch,
        automationId: 'product-cartridge-product-list',
        showPaneLoader,
        showImageLoader,
        showPricing,
        deviceType,
        eligibilityMessage: (priceDetails || {}).eligibilityMessage || '',
        isCouponRecommendationsModal,
        enableGridViewInCertona,
        defaultOffSet: getDefaultOffSet('galleryScrollToOffSet', false) || 1000,
        setImageHeight: index === 0 ? (el) => setImageHeight(el) : null,
        fromMiniPDP,
    });

    const setImageHeight = useCallback((el) => {
        el && setImgHeight(el.offsetHeight);
    }, []);

    const productCardRenderer = (dataItem, index) => {
        return customRenderProductCard
            ? cartridgeListRenderer(dataItem, context, highlightCardSlot, setImageHeight)
            : defaultProductCardRenderer(dataItem, index);
    };

    const isPriceEmpty = (item) => {
        const { originalMax, originalMin, currentMin, currentMax } = item;
        const isZeroOrUndefined = (value) => !value || value === '0';
        return [originalMax, originalMin, currentMin, currentMax].every(isZeroOrUndefined);
    };

    const fireEnsightenEvent = (productList) => {
        const ppIds = productList.reduce((result, item) => {
            const { priceDetails, ppId, id } = item;
            const shouldPushId =
                (panelType === 'HOTDEAL' && isPriceEmpty(item)) ||
                (panelType === 'CERTONA' && !priceDetails);
            if (shouldPushId) result.push(ppId || id);
            return result;
        }, []);

        if (ppIds.length > 0) {
            dispatch(
                triggerEnsightenEvent({
                    pageType,
                    errorType: 'Price Not Found',
                    errorDescription: `${
                        cartridgeHeader || panelTitle
                    }: No price info for ${ppIds.join(',')}`,
                })
            );
        }
    };

    const carouselItemList = (dataItems) => {
        return dataItems?.map((item, index) => {
            item.index = item.index ?? index;
            return productCardRenderer(item, index);
        });
    };

    useProductCartridgeEffect(
        productCartridgeList,
        enableUrgencyMessagingOnATC,
        dispatch,
        pathname,
        slotId,
        cartridgeRef,
        fireEnsightenEvent
    );

    let titleGrid = '';
    let cartridgeGrid = '';
    if (isPLAEnabled) {
        titleGrid = 'lg2 xl2';
        cartridgeGrid = 'lg10 xl10';
    }
    const productCartridgeLength = productCartridgeList?.length;

    const carouselTheme = () => {
        if (fromMiniPDP || pageType === 'ADDTOCART') return 'inslider-carousel';
        if (fromHotDeals) return 'hotdeals-carousel';
        if (fromCart) return 'cart-carousel';
        return 'rrzones-carousel';
    };

    const listStyleClass = dt(['p-0', '!flex', 'flex-wrap']);
    const listItemStyle = dt([
        'lg:w-1/4',
        'md:w-1/3',
        'sm:w-1/2',
        '!p-2',
        'smOnly:odd:!pl-0',
        'smOnly:even:!pr-0',
    ]);
    const girdViewInCertona = pageType === 'ADDTOCART' && enableGridViewInCertona;
    const titleGridStyle = girdViewInCertona ? dt(['md:!pl-2']) : '';
    const titleTextStyle = girdViewInCertona ? dt(['!pb-0']) : '';

    return productCartridgeLength ? (
        <section
            className={cx('productCartridge', productCatridgeTheme, {
                newProdCardWrapper: !disableFPACAPI,
            })}
            ref={(wrapper) => {
                if (!isPLAEnabled && wrapper) cartridgeRef = wrapper;
            }}
        >
            {hasTitle && (
                <h5
                    data-automation-id="product-catridge-title"
                    className={cx(
                        'productCartridgeTitle',
                        titleGrid,
                        { 'pdp-title': pageType === 'PRODUCT', titlePLA: isPLAEnabled },
                        titleGridStyle,
                        titleTextStyle
                    )}
                >
                    {cartridgeHeader}
                </h5>
            )}
            {girdViewInCertona ? (
                <List
                    datasource={productCartridgeList}
                    listStyleClass={listStyleClass}
                    itemStyleClass={listItemStyle}
                    childRenderer={productCardRenderer}
                    automationId="product-cartridge-list"
                    spacing="None"
                />
            ) : (
                <div className={cx('prodCatridgeListBlock', cartridgeGrid)} id={slotId}>
                    <Carousel
                        theme={carouselTheme()}
                        automationId="reczoneCarousel"
                        automationItemId="reczones-item"
                        children={carouselItemList(productCartridgeList)}
                        contentWrapperClass={dt(['smMd:px-6', 'smMd:pb-3'])}
                        carouselImageHeight={imageHeight}
                    />
                </div>
            )}
        </section>
    ) : null;
};

YodaProductCartridge.defaultProps = {
    customRenderProductCard: false,
    cartridgeListRenderer: null,
    productCatridgeTheme: '',
    cartridgeHeader: '',
    onPaneClick: false,
    hasTitle: true,
    isPLAEnabled: false,
    disableLazyLoad: false,
    showPaneLoader: false,
    showImageLoader: false,
    pageType: '',
    panelType: '',
    panelTitle: '',
    pricingConfig: {},
    actions: {},
    showPricing: true,
    productItemClass: '',
    enableGridViewInCertona: false,
    enableUrgencyMessagingOnATC: false,
    highlightCardSlot: false,
    isCouponRecommendationsModal: false,
    fromMiniPDP: false,
    criteriaName: '',
    fromHotDeals: false,
    fromCart: false,
};

YodaProductCartridge.propTypes = {
    slotId: PropTypes.string.isRequired,
    cartridgeListRenderer: PropTypes.func,
    disableLazyLoad: PropTypes.bool,
    customRenderProductCard: PropTypes.bool,
    productCatridgeTheme: PropTypes.string,
    productCartridgeList: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
    cartridgeHeader: PropTypes.string,
    onPaneClick: PropTypes.func,
    isPLAEnabled: PropTypes.bool,
    showPaneLoader: PropTypes.bool,
    showImageLoader: PropTypes.bool,
    pageType: PropTypes.string,
    panelType: PropTypes.string,
    panelTitle: PropTypes.string,
    pricingConfig: PropTypes.object,
    showPricing: PropTypes.bool,
    productItemClass: PropTypes.string,
    actions: PropTypes.object,
    enableGridViewInCertona: PropTypes.bool,
    enableUrgencyMessagingOnATC: PropTypes.bool,
    highlightCardSlot: PropTypes.bool,
    hasTitle: PropTypes.bool,
    isCouponRecommendationsModal: PropTypes.bool,
    fromMiniPDP: PropTypes.bool,
    criteriaName: PropTypes.string,
    location: PropTypes.object.isRequired,
    fromHotDeals: PropTypes.bool,
    fromCart: PropTypes.bool,
};

/* istanbul ignore next */
export default YodaProductCartridge;
